import { Theme } from '@css-modules-theme/core'
import classNames from 'classnames'
import moment from 'moment-timezone'
import React, { FunctionComponent, useCallback, useContext, useMemo } from 'react'
import { useOurStore } from '@/app/_providers/StoreProvider'
import {
  getHasReachedLimit,
  getIsMemberOnlyCategory,
  getRemainingForCategory,
  handleQtyClick,
  hasMembership,
} from '@/utils/components/tickets.utils'
import { getSalesWindowData } from '@/utils/components/order-tickets.utils'
import OrderTicket from '@/app/_components/OrderItem/OrderItem'
import StringToReactNode from '@/app/_components/StringToReactNode/StringToReactNode'
import TXT from '@/messages'
import AdditionalInfoBlock from '@/app/_components/AdditionalInfo/AdditionalInfoBlock'
import LoginContext from '@/app/_providers/LoginProvider/LoginContext'
import { useCart } from '@/app/_providers/CartProvider/CartProvider'
import { getUnsettledQtyForCategory } from '@/utils/cart.utils'

type Props = {
  items: readonly Transformed.TicketCategoryWithTiers[];
  timeslotId: string;
  itemType: 'tickets' | 'addons';
  session: Transformed.EventTicketsAndAddonsWithTimeslots;
  qtyForTier: (args: QtyArgs) => number;
  thisEvent: Transformed.EventWithTimeslotsAndMeta;
  thisCartUpdating: boolean;
  setAddlInfoResponses: React.Dispatch<DataAccessInput.AdditionalInfoResponse[]>;
  styles: Theme
}

type HandleClickCallback = (args: {
  action: QtyClickAction
  tier: Transformed.TicketTier,
  ticketCategory: Transformed.TicketCategoryWithTiers,
  qtyForTier: (args: QtyArgs) => number,
  event: Transformed.EventWithTimeslotsAndMeta,
  maxPerCategory: number,
  remaining: number,
}) => void

const OrderItemsSection: FunctionComponent<Props> = ({
  items,
  timeslotId,
  itemType,
  session,
  qtyForTier,
  thisEvent,
  thisCartUpdating,
  setAddlInfoResponses,
  styles,
}) => {
  const updatePartial = useOurStore((state) => state.updatePartial)
  const localLineItems = useOurStore((state) => state.localLineItems)
  const lineItemsToDelete = useOurStore((state) => state.lineItemsToDelete)
  const { cart } = useCart()
  const { membership } = useContext(LoginContext)

  /**
   * Get the quantity of tickets (excluding add-ons) from the list of line-items to delete
   */
  const ticketsToDeleteQuantity = useMemo(() => (
    lineItemsToDelete
    // filter for add-ons
      .filter((el) => el.category.meta.every((meta) => meta.metakey !== 'addons'))
    // get quantity for this tier only
      .reduce((acc, cv) => cv.quantity + acc, 0)
  ), [lineItemsToDelete])

  /**
   * Get the quantity of tickets (excluding add-ons) in cart and local
   * Subtract tickets to delete
   */
  const unsettledTicketQuantity = useMemo(() => (
    itemType === 'addons'
      ? (
        [...cart.lineItems, ...localLineItems]
        // filter for add-ons
          .filter((el) => el.category.meta.every((meta) => meta.metakey !== 'addons'))
        // get quantity for this tier only
          .reduce((acc, cv) => cv.quantity + acc, 0)
      // subtract tickets to delete
      ) - ticketsToDeleteQuantity
      : 0
  ), [cart.lineItems, itemType, localLineItems, ticketsToDeleteQuantity])

  const handleClickCallback: HandleClickCallback = useCallback(({
    action,
    tier,
    ticketCategory,
    event,
    maxPerCategory,
    remaining,
  }) => {
    const {
      updatedLocalLineItems,
      updatedLineItemsToDelete,
    } = handleQtyClick({
      action,
      tier,
      category: ticketCategory,
      localLineItems,
      cartLineItems: cart.lineItems,
      lineItemsToDelete,
      adjustedQtyForTier: qtyForTier,
      remaining,
      event,
      maxPerOrder: -1,
      maxPerCategory,
      timeslot: session.timeslot,
      limitByCartQty: false,
      addOnsCategories: session.addons || null,
    })

    updatePartial({
      localLineItems: updatedLocalLineItems,
      lineItemsToDelete: updatedLineItemsToDelete,
    })
  }, [session.addons, session.timeslot, localLineItems, cart.lineItems, lineItemsToDelete, qtyForTier, updatePartial])

  // if member-only category, limit by the member purchase limits if they exist
  // otherwise, if it's an addon, limit by the number of tickets in the cart
  // else, limit by the max per category
  const getMaxLimitForTier = useCallback((
    tier: Transformed.TicketTier,
    maxPerCategory: number,
    isMemberOnlyCategory: boolean,
  ): number => {
    if (isMemberOnlyCategory) {
      const memberPurchaseLimits = membership?.purchaseLimits.find((limit) => limit?.key === tier.title)?.value
      return memberPurchaseLimits || -1
    }

    // if addons, allow each tier of addons to equal the unsettled ticket quantity
    // 2 tickets allows 2 addons for each tier
    if (itemType === 'addons') {
      return unsettledTicketQuantity
    }

    return maxPerCategory
  }, [itemType, membership?.purchaseLimits, unsettledTicketQuantity])

  return (
    <>
      {items.map((category, itemIndex) => {
        // hide category if item or tiers is somehow falsy
        if (!category || !category.tiers.length) {
          return null
        }

        // hide category if capacity is 0
        if (category.capacity.total === 0) {
          return null
        }

        const isMemberOnlyCategory = getIsMemberOnlyCategory(category)

        // hide category if membership category and the user is not a member
        if (isMemberOnlyCategory && !hasMembership(membership)) {
          return null
        }

        const {
          categoryOnSale,
          salesWindowData,
          timeslotEndOffsetMoment,
          startOffsetMoment,
          endOffsetMoment,
        } = getSalesWindowData(category, timeslotId, session.timeslot.endTimeWithOffset)

        /**
         * if the sales window is elapsed don't show
         */
        if (salesWindowData?.elapsed || timeslotEndOffsetMoment?.isBefore(moment.now())) {
          return null
        }

        /**
         * If category is not on sale and it's a day of show group
         */
        if (!categoryOnSale && salesWindowData?.isDOS) {
          return null
        }

        const remaining = getRemainingForCategory(
          itemType === 'addons' ? -1 : session.timeslot.capacity.remaining, // don't limit addons to session capacity
          category,
        )

        const maxPerCategory = category.maxPerOrder || -1

        const unsettledQuantityForCategory = getUnsettledQtyForCategory({
          id: category.id,
          localLineItems,
          cartLineItems: cart.lineItems,
          lineItemsToDelete,
        })

        const categoryHasAddlInfo = !!category?.additionalInfo?.length
        const purchaseLimitRoles = membership?.purchaseLimits.map((limit) => limit?.key)

        const orderItems = category.tiers.map((tier, tierIndex) => {
          // if it is a member only category and the user doesn't have the right role, don't show the tier
          if (
            isMemberOnlyCategory && !purchaseLimitRoles?.includes(tier?.title)
            || !tier
          ) {
            return null
          }

          const maxLimitForTier = getMaxLimitForTier(
            tier,
            maxPerCategory,
            isMemberOnlyCategory,
          )

          const quantityForTier = qtyForTier({
            categoryId: category.id,
            tierId: tier.id,
            timeslotId,
          })
          const membershipTicketLimit = isMemberOnlyCategory ? maxLimitForTier : undefined
          const hasReachedLimit = getHasReachedLimit({
            quantityForTier,
            quantityForCategory: unsettledQuantityForCategory,
            maxLimitForTier,
            maxLimitForCategory: -1,
            remainingQuantity: remaining,
            membershipTicketLimit,
          })

          return (
            <div
              key={`${tier.id}-${category.id}`}
              className={classNames({ [styles.EventTierWrapper]: itemType === 'tickets' })}
              data-testid="ticket-tier-wrapper"
            >
              <OrderTicket
                className={classNames({ [styles.TicketContainer]: itemType === 'tickets' })}
                describedBy={`${itemType === 'addons' ? 'addons' : 'events'}-title`}
                quantity={quantityForTier}
                remaining={remaining}
                hasReachedLimit={hasReachedLimit}
                dataTestId={`order-item-${itemIndex}-${tierIndex}`}
                category={category}
                startTime={session.timeslot.startTime}
                title={tier.title}
                {...(tier.subtitle ? { note: tier.subtitle } : {})}
                price={tier.price}
                handleClick={(action) => handleClickCallback({
                  action,
                  tier,
                  ticketCategory: category,
                  qtyForTier,
                  event: thisEvent,
                  maxPerCategory,
                  remaining,
                })}
                onSaleOrWithinSalesWindow={categoryOnSale}
                cartUpdating={thisCartUpdating}
                startOffset={startOffsetMoment}
                endOffset={endOffsetMoment}
              />
            </div>
          )
        })

        return (
          <div key={`${category.id}`}>
            {category.subtitle && (
              <StringToReactNode
                data-testid="category-subtitle"
                data-variant="events"
                className={classNames(styles.CategoryDescription)}
                incomingString={category.subtitle}
              />
            )}
            {itemType === 'tickets'
              ? (
                <>
                  {orderItems}
                  {maxPerCategory > -1 && unsettledQuantityForCategory >= maxPerCategory && (
                    <div
                      aria-atomic="true"
                      aria-live="polite"
                      className={styles.MaxTicketsContainer}
                      data-testid="max-tickets-container"
                      tabIndex={0}
                    >
                      {TXT.alerts.maxTickets(maxPerCategory, category.title)}
                    </div>
                  )}
                </>
              )
              : (
                <div data-testid="event-addons-container">
                  <h2 className={classNames(styles.EventCategoryHeader, styles.AddOnsHeader)}>
                    {TXT.titles.steps.orderAddOns}
                  </h2>
                  <div className={styles.AddOnsContainer}>{orderItems}</div>
                </div>
              )}
            {itemType === 'tickets' && (
              <AdditionalInfoBlock
                visibleStatus={{ [category.id]: unsettledQuantityForCategory > 0 && categoryHasAddlInfo }}
                categories={[category]}
                tiers={category.tiers as Transformed.TicketTier[]}
                setValues={setAddlInfoResponses}
                isEvents
              />
            )}
          </div>
        )
      })}
    </>
  )
}

export default OrderItemsSection
